import type { PracticeLocale } from "./practiceLocale";

/** Kindergarten is 0. Grades 1–12 follow. */
export const PRACTICE_GRADE_MIN = 0;
export const PRACTICE_GRADE_MAX = 12;

export const PRACTICE_GRADES: readonly number[] = [
  0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12,
];

/** Hebrew letter for grades 1–12. Index 0 is unused. */
const HEBREW_GRADE_LETTER = [
  "",
  "א",
  "ב",
  "ג",
  "ד",
  "ה",
  "ו",
  "ז",
  "ח",
  "ט",
  "י",
  "יא",
  "יב",
] as const;

export function isPracticeGrade(value: unknown): value is number {
  return (
    typeof value === "number" &&
    Number.isInteger(value) &&
    value >= PRACTICE_GRADE_MIN &&
    value <= PRACTICE_GRADE_MAX
  );
}

export function parsePracticeGrade(value: unknown): number | null {
  if (typeof value === "number") return isPracticeGrade(value) ? value : null;
  if (typeof value !== "string" || !value.trim()) return null;
  const n = Number(value);
  return isPracticeGrade(n) ? n : null;
}

export function gradeSpanIncludes(
  grade: number,
  gradeMin: number,
  gradeMax: number,
): boolean {
  return grade >= gradeMin && grade <= gradeMax;
}

/** Compact chip: "K" / "8", or "גן" / "ח". */
export function gradeShortLabel(grade: number, locale: PracticeLocale): string {
  if (!isPracticeGrade(grade)) return String(grade);
  if (locale === "he") return grade === 0 ? "גן" : HEBREW_GRADE_LETTER[grade];
  return grade === 0 ? "K" : String(grade);
}

/** Full label: "Kindergarten" / "Grade 8", or "גן חובה" / "כיתה ח". */
export function gradeFullLabel(grade: number, locale: PracticeLocale): string {
  if (!isPracticeGrade(grade)) return String(grade);
  if (locale === "he") {
    return grade === 0 ? "גן חובה" : `כיתה ${HEBREW_GRADE_LETTER[grade]}`;
  }
  return grade === 0 ? "Kindergarten" : `Grade ${grade}`;
}

/** Span label: "Grades 7–8" / "כיתות ז–ח". A single grade uses the full label. */
export function gradeSpanLabel(
  gradeMin: number,
  gradeMax: number,
  locale: PracticeLocale,
): string {
  if (!isPracticeGrade(gradeMin) || !isPracticeGrade(gradeMax)) return "";
  const min = Math.min(gradeMin, gradeMax);
  const max = Math.max(gradeMin, gradeMax);
  if (min === max) return gradeFullLabel(min, locale);
  if (locale === "he") {
    if (min === 0) {
      return `גן חובה–כיתה ${HEBREW_GRADE_LETTER[max]}`;
    }
    return `כיתות ${HEBREW_GRADE_LETTER[min]}–${HEBREW_GRADE_LETTER[max]}`;
  }
  const a = min === 0 ? "K" : String(min);
  return `Grades ${a}–${max}`;
}
